import {Box, LoadingOverlay, Select, TextInput} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import {WhiteBgButton} from '@packages/ui';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useState} from 'react';
import type {z} from 'zod';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {HOME} from '../../../config/constants/routes';
import isAppWebView from '../../../config/isAppWebView';
import {
  InquiryForeignUnivFormSchema,
  type PostInquiryForeignUnivSchema,
} from '../../../core/entities/inquiry/schema';
import {usePostInquiryForeignUniv} from '../../../core/repositories/inquiryRepository';
import {useReCaptcha} from '../../../core/usecases/useReCaptcha';
import type {NextPageWithLayout} from '../../../pages/_app';
import {SectionTitle} from '../../../shared/components/SectionTitle';
import {showErrorSnackBar} from '../../../shared/components/SnackBar';
import {RequiredTag} from './Container/Presenter/RequiredTag';
import {RegisterLayout} from './RegisterLayout';

// PathはInquiryだが、UIのパーツの依存関係的にRegisterに置いた

export const RegisterForeignUnivPage: NextPageWithLayout = () => {
  const {reCaptcha} = useReCaptcha();
  const router = useRouter();
  const introduceId = router.query.introduce_id
    ? String(router.query.introduce_id)
    : undefined;
  const campaignId = router.query.campaign_id
    ? String(router.query.campaign_id)
    : undefined;

  const form = useForm<z.infer<typeof InquiryForeignUnivFormSchema>>({
    validate: zodResolver(InquiryForeignUnivFormSchema),
    initialValues: {
      name: '',
      email: '',
      univ_name: '',
      program: '',
      degree: '',
      degree_other: '',
      url: '',
      country_name: '',
    },
  });
  const [isLoading, handlers] = useDisclosure(false);
  const postInquiryForeignUniv = usePostInquiryForeignUniv({
    onSuccessFnc() {
      setSubmit({
        isSubmit: true,
        submitMailAddress: form.values.email,
      });
      handlers.close();
    },
    onErrorFnc(error: any) {
      showErrorSnackBar(error);
      handlers.close();
    },
  });

  const [{isSubmit, submitMailAddress}, setSubmit] = useState<{
    isSubmit: boolean;
    submitMailAddress: string;
  }>({
    isSubmit: false,
    submitMailAddress: '',
  });

  const onSubmit = () => {
    const result = form.validate();
    if (result.hasErrors) return;

    const postData: z.infer<typeof PostInquiryForeignUnivSchema> = {
      ...form.values,
    };
    if (String(introduceId)) {
      postData.introducer_id = introduceId;
    }

    if (String(campaignId)) {
      postData.user_introduce_campaign_id = campaignId;
    }

    // 学位の選択肢とその他の入力内容を結合
    if (form.values.degree === 'その他') {
      postData.degree = `${form.values.degree}: ${form.values.degree_other}`;
    }

    if (isAppWebView()) {
      postInquiryForeignUniv.mutate(postData);
    } else {
      reCaptcha('submit')
        .then(() => {
          handlers.open();
          postInquiryForeignUniv.mutate(postData);
        })
        .catch(() => {
          showErrorSnackBar({
            isNetworkError: false,
            title: 'reCAPTCHAエラー',
            message: '再度お試しください',
          });
        });
    }
  };

  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const headTitle = '海外大からのユーザー登録 | 外資就活ドットコム';
  return (
    <>
      <Head>
        <title key="title">{headTitle}</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta key="og:title" property="og:title" content={`${headTitle}`} />
        <meta key="og:url" property="og:url" content={currentUrl} />
      </Head>
      <RegisterLayout>
        <Box className="w-full max-w-[664px]">
          {!isSubmit && (
            <>
              <SectionTitle order={2} title={'海外大からのユーザー登録'} />
              <Box className="relative mb-16 bg-white px-16 pc:px-8 py-16 pc:px-32">
                <LoadingOverlay visible={isLoading} />
                <Box className="py-16">
                  <p>
                    フリーアドレスは受け付けておりませんので、予めご了承ください。
                  </p>
                </Box>

                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="m-4 text-base">お名前</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('name')}
                  />
                </Box>
                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="m-4 text-base">
                      大学発行メールアドレス
                    </span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('email')}
                  />
                </Box>
                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="p-4 text-base">大学名</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('univ_name')}
                  />
                </Box>
                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="p-4 text-base">在籍プログラム</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('program')}
                  />
                </Box>
                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="p-4 text-base">学位</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <Select
                    className="grow pc:mx-16"
                    data={[
                      '経営学修士',
                      '修士・大学院',
                      '学士・大学',
                      '法務博士',
                      '博士',
                      '準学士・短大',
                      '交換留学',
                      'その他',
                    ]}
                    {...form.getInputProps('degree')}
                  />
                </Box>

                {form.values.degree === 'その他' && (
                  <Box className="pc:flex p-4 mb-8">
                    <Box className="pc:w-1/3 flex pc:justify-between m-8" />
                    <TextInput
                      placeholder="その他の学位を入力"
                      className="grow pc:mx-16"
                      {...form.getInputProps('degree_other')}
                    />
                  </Box>
                )}

                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="p-4 text-base">大学のWebサイトURL</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('url')}
                  />
                </Box>
                <Box className="pc:flex p-4">
                  <Box className="pc:w-1/3 flex pc:justify-between m-8">
                    <span className="p-4 text-base">国名</span>
                    <Box className="h-28">
                      <RequiredTag />
                    </Box>
                  </Box>
                  <TextInput
                    className="grow pc:mx-16"
                    {...form.getInputProps('country_name')}
                  />
                </Box>
                <Box className="flex justify-center my-16">
                  <WhiteBgButton
                    type="submit"
                    label={'送信'}
                    isPrimary={false}
                    className="w-2/3"
                    onClick={() => {
                      onSubmit();
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
          {isSubmit && (
            <>
              <SectionTitle
                order={2}
                title={'お問い合わせありがとうございます'}
              />
              <Box className="mb-16 bg-white px-16 pc:px-8 py-16 pc:px-32">
                <p>
                  ご入力いただいた以下のメールアドレス宛に、確認メールを送信しました。
                </p>
                <p className="p-4">{submitMailAddress}</p>
                <p>
                  確認メールが届かない場合は、入力されたメールアドレスに誤りがある可能性がございます。
                </p>
                <p>お手数ですが改めてお問い合わせください。</p>
                <p>
                  なお、頂戴いたしましたお問い合わせにつきましては、内容を確認の上、後ほどご回答いたします。
                </p>
                <p>
                  また、お問い合わせの内容によっては、ご回答まで数日かかる場合やご回答いたしかねる場合がございますので、予めご了承ください。
                </p>
                <a
                  href={HOME}
                  className="hover:underline hover:decoration-primary-2 text-primary-2"
                >
                  トップに戻る
                </a>
              </Box>
            </>
          )}
        </Box>
      </RegisterLayout>
    </>
  );
};
